import React, { useState } from "react";
import "../styles/StudentRegistration.css";
import { registerStudent } from "../api/StudentDetails";
import validateUser from "../utility/uservalidation";
import Loader from "../utility/FullPageLoader";
import {
  StudentRegistrationData,
  FormErrors,
} from "../interfaces/StudentInterface";
import { LoadingModel } from "../interfaces/LoadingInterface";
import CustomDatePicker from "./CustomDatePicker";

const MONTHLY_AMOUNT = 500;
const DAYS_PER_MONTH = 31;
const MIN_AMOUNT = 200;

const addDays = (dateStr: string, days: number): string => {
  const [year, month, day] = dateStr.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const calculateGrantedDays = (amount: number): number => {
  if (!amount || amount < MIN_AMOUNT) {
    return 0;
  }
  return Math.floor((amount * DAYS_PER_MONTH) / MONTHLY_AMOUNT);
};

const resolveValidityTo = (validityFrom: string, amount: string): string => {
  const amountValue = parseFloat(amount);
  const grantedDays = calculateGrantedDays(
    Number.isNaN(amountValue) ? 0 : amountValue
  );
  if (!validityFrom || grantedDays <= 0) {
    return "";
  }
  return addDays(validityFrom, grantedDays);
};

const emptyRegistrationForm = (): StudentRegistrationData => ({
  firstName: "",
  lastName: "",
  emailId: "",
  aadharNumber: "",
  optionalIdentification: "",
  mobileNumber: "",
  alternateNumber: "",
  guardianName: "",
  guardianMobileNumber: "",
  genderCode: "",
  address: "",
  joiningDate: "",
  amount: "500",
  validityFrom: "",
  validityTo: "",
  photo: null,
});

const StudentRegistration: React.FC = () => {
  const [formData, setFormData] = useState<StudentRegistrationData>(
    emptyRegistrationForm()
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null); // ✅ Add this
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user"); // 'user' = front, 'environment' = back
  const [regLoading, setRegLoading] = useState<LoadingModel>({
    isLoading: false,
    message: "",
  });

  const validateForm = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.emailId.trim()) {
      newErrors.emailId = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = "Please enter a valid email address";
    } else if (await validateUser("EmailId", formData.emailId)) {
      newErrors.emailId = "This email is already registered";
    }
    if (!formData.aadharNumber.trim()) {
      newErrors.aadharNumber = "Aadhar Number is required";
    } else if (!/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ""))) {
      newErrors.aadharNumber = "Please enter a valid 12-digit Aadhar number";
    } else if (await validateUser("AadharNumber", formData.aadharNumber)) {
      newErrors.aadharNumber = "This Aadhar Number is already registered";
    }

    if (!formData.genderCode.trim()) {
      newErrors.genderCode = "Gender is required";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (formData.mobileNumber.trim().startsWith("+91")) {
      newErrors.mobileNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    } else if (await validateUser("MobileNumber", formData.mobileNumber)) {
      newErrors.mobileNumber = "This Mobile Number is already registered";
    }

    if (
      formData.alternateNumber.trim() &&
      !/^\+?[\d\s-()]{7,15}$/.test(formData.alternateNumber)
    ) {
      newErrors.alternateNumber =
        "Please enter a valid alternate mobile number";
    } else if (formData.alternateNumber.trim().startsWith("+91")) {
      newErrors.alternateNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    }

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = "Guardian name is required";
    }

    if (!formData.guardianMobileNumber.trim()) {
      newErrors.guardianMobileNumber = "Guardian mobile number is required";
    } else if (formData.guardianMobileNumber.trim().startsWith("+91")) {
      newErrors.guardianMobileNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    } else if (!/^\+?[\d\s-()]{7,15}$/.test(formData.guardianMobileNumber)) {
      newErrors.guardianMobileNumber =
        "Please enter a valid guardian mobile number";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.joiningDate.trim()) {
      newErrors.joiningDate = "Joining date is required";
    }

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required";
    } else {
      const amountValue = parseFloat(formData.amount);
      const grantedDays = calculateGrantedDays(
        Number.isNaN(amountValue) ? 0 : amountValue
      );
      if (Number.isNaN(amountValue) || amountValue < MIN_AMOUNT) {
        newErrors.amount = `Amount must be at least ₹${MIN_AMOUNT}`;
      } else if (grantedDays <= 0) {
        newErrors.amount = "Amount is too low to grant any days";
      }
    }

    if (!formData.validityFrom.trim()) {
      newErrors.validityFrom = "Validity from date is required";
    }

    if (!formData.validityTo.trim()) {
      newErrors.validityTo =
        "Enter amount and validity from date to calculate validity to date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        photo: "Please select a valid image file",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB max
      setErrors((prev) => ({
        ...prev,
        photo: "Image size must be less than 5MB",
      }));
      return;
    }

    // ✅ Save real file (for backend)
    setFormData((prev) => ({ ...prev, photo: file }));
    setErrors((prev) => ({ ...prev, photo: "" }));

    // ✅ Generate preview
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const startCamera = async (mode?: "user" | "environment") => {
    try {
      // Stop existing stream if any
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }

      const cameraMode = mode || facingMode;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: cameraMode,
        },
      });
      setCameraStream(stream);
      setFacingMode(cameraMode);
      setShowCamera(true);
    } catch (error) {
      console.error("Error accessing camera:", error);
      setErrors((prev) => ({
        ...prev,
        photo: "Unable to access camera. Please check permissions.",
      }));
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const switchCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    await startCamera(newMode);
  };
  // Ensure your state type is: photo: File | null
  // setFormData: React.Dispatch<React.SetStateAction<StudentRegistrationData>>

  const capturePhoto = async () => {
    const video = document.getElementById(
      "camera-video"
    ) as HTMLVideoElement | null;
    if (!video) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // make sure video has dimensions
    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0, width, height);

    // Generate preview data URL for display
    const previewDataURL = canvas.toDataURL("image/jpeg", 0.8);
    setPhotoPreview(previewDataURL);

    // Prefer toBlob (smaller memory than dataURL)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8)
    );

    if (!blob) {
      // Fallback if toBlob not supported (very rare)
      const dataURL = canvas.toDataURL("image/jpeg", 0.8);
      const fallbackBlob = dataUrlToBlob(dataURL);
      if (!fallbackBlob) return;
      const fallbackFile = new File([fallbackBlob], makePhotoName(), {
        type: "image/jpeg",
      });
      setFormData((prev) => ({ ...prev, photo: fallbackFile }));
      setErrors((prev) => ({ ...prev, photo: "" }));
      stopCamera();
      return;
    }

    const file = new File([blob], makePhotoName(), { type: "image/jpeg" });

    setFormData((prev) => ({ ...prev, photo: file })); // <-- File, not string
    setErrors((prev) => ({ ...prev, photo: "" }));
    stopCamera();
  };

  // Helper: generate a filename
  const makePhotoName = () => `capture_${Date.now()}.jpg`;

  // Helper: convert dataURL -> Blob (fallback path)
  function dataUrlToBlob(dataURL: string): Blob | null {
    const parts = dataURL.split(",");
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const byteString = atob(parts[1]);
    const len = byteString.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = byteString.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }
  const removePhoto = () => {
    setFormData((prev) => ({ ...prev, photo: null }));
    setPhotoPreview(null);
    setErrors((prev) => ({ ...prev, photo: "" }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Prepare the value we will save (default = raw value)
    let newValue = value;

    // Format Aadhar number as user types (XXXX XXXX XXXX) - max 12 digits
    if (name === "aadharNumber") {
      newValue = value
        .replace(/\D/g, "") // remove non-digits
        .slice(0, 12) // limit to 12 digits
        .replace(/(\d{4})(?=\d)/g, "$1 "); // insert space after every 4 digits
    }

    // Format Mobile Number as user types (XXXXX XXXXX) - max 10 digits
    else if (
      name === "alternateNumber" ||
      name === "mobileNumber" ||
      name === "guardianMobileNumber"
    ) {
      newValue = value
        .replace(/\D/g, "") // remove non-digits
        .slice(0, 10); // limit to 10 digits
    }

    // Set form data once
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      if (name === "amount") {
        updated.validityTo = resolveValidityTo(updated.validityFrom, newValue);
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getGrantedDays = (): number => {
    const amountValue = parseFloat(formData.amount);
    return calculateGrantedDays(Number.isNaN(amountValue) ? 0 : amountValue);
  };

  const handleValidityFromChange = (date: string) => {
    setFormData((prev) => ({
      ...prev,
      validityFrom: date,
      validityTo: resolveValidityTo(date, prev.amount),
    }));

    setErrors((prev) => ({
      ...prev,
      validityFrom: "",
      validityTo: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // avoid double submit
    if (isSubmitting) return;

    // 1) validate first
    const isValid = await validateForm();
    setRegLoading({ isLoading: true, message: "Validating form data..." });
    if (!isValid) {
      setRegLoading({ isLoading: false, message: "" });
      return;
    }

    // 2) only after validation passes, submit
    setIsSubmitting(true);
    setErrors({});

    try {
      console.log("Submitting form data:", formData);
      setRegLoading({ isLoading: true, message: "Submitting form data..." });
      // real call:
      await registerStudent({
        ...formData,
        validityTo: resolveValidityTo(formData.validityFrom, formData.amount),
      });

      // or demo delay:
      // await new Promise((resolve) => setTimeout(resolve, 1500));

      // 3) reset on success
      setFormData(emptyRegistrationForm());
      setPhotoPreview(null);
      setIsSubmitted(true);

      // auto-hide success
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({
        general: "Failed to register student. Please try again." + error,
      });
    } finally {
      setIsSubmitting(false);
      setRegLoading({ isLoading: false, message: "" });
    }
  };

  const resetForm = () => {
    setFormData(emptyRegistrationForm());
    setErrors({});
    setIsSubmitted(false);
    setPhotoPreview(null);
    stopCamera();
  };

  return (
    <div className="student-registration">
      {regLoading.isLoading && <Loader message={regLoading.message} />}
      <div className="registration-header">
        <h2>Student Registration</h2>
        <p>Register new students in the library management system</p>
      </div>

      <div className="registration-card">
        {isSubmitted && (
          <div className="success-banner">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h4>Student Registered Successfully!</h4>
              <p>
                The student has been added to the system and can now be
                allocated a seat.
              </p>
            </div>
          </div>
        )}

        {errors.general && (
          <div className="error-banner">
            <div className="error-icon">⚠️</div>
            <div className="error-content">
              <h4>Registration Failed</h4>
              <p>{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-header-section">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? "error" : ""}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? "error" : ""}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emailId">Email Address *</label>
                  <input
                    type="email"
                    id="emailId"
                    name="emailId"
                    value={formData.emailId}
                    onChange={handleInputChange}
                    className={errors.emailId ? "error" : ""}
                    placeholder="student@university.edu"
                  />
                  {errors.emailId && (
                    <span className="error-message">{errors.emailId}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="mobileNumber">Mobile Number *</label>
                  <input
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    className={errors.mobileNumber ? "error" : ""}
                    placeholder="+1-555-0123"
                  />
                  {errors.mobileNumber && (
                    <span className="error-message">{errors.mobileNumber}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="joiningDate">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    >
                      <path
                        d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Joining Date *
                  </label>
                  <CustomDatePicker
                    value={formData.joiningDate}
                    onChange={(date) =>
                      setFormData((prev) => {
                        const validityFrom = prev.validityFrom || date;
                        return {
                          ...prev,
                          joiningDate: date,
                          validityFrom,
                          validityTo: resolveValidityTo(validityFrom, prev.amount),
                        };
                      })
                    }
                    error={errors.joiningDate}
                    placeholder="Select joining date"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="amount">Amount (₹) *</label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter amount"
                    min={MIN_AMOUNT}
                    step="1"
                    className={errors.amount ? "error" : ""}
                    disabled={isSubmitting}
                  />
                  {errors.amount && (
                    <span className="error-message">{errors.amount}</span>
                  )}
                  <span className="field-note">
                    ₹{MONTHLY_AMOUNT} = {DAYS_PER_MONTH} days. Minimum ₹
                    {MIN_AMOUNT}.
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="validityFrom">Validity From Date *</label>
                  <CustomDatePicker
                    value={formData.validityFrom}
                    onChange={handleValidityFromChange}
                    error={errors.validityFrom}
                    placeholder="Select from date"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="validityTo">Validity To Date</label>
                  <input
                    type="text"
                    id="validityTo"
                    name="validityTo"
                    value={formData.validityTo || "Enter amount and from date"}
                    readOnly
                    className="readonly-field"
                    disabled
                  />
                  <span className="field-note">
                    {getGrantedDays() > 0
                      ? `${getGrantedDays()} Days`
                      : `Enter ₹${MIN_AMOUNT} or more to calculate days`}
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="aadharNumber">Aadhar Number *</label>
                  <input
                    type="text"
                    id="aadharNumber"
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleInputChange}
                    className={errors.aadharNumber ? "error" : ""}
                    placeholder="1234 5678 9012"
                    maxLength={14}
                  />
                  {errors.aadharNumber && (
                    <span className="error-message">{errors.aadharNumber}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="genderCode">Gender *</label>
                  <select
                    id="genderCode"
                    name="genderCode"
                    value={formData.genderCode}
                    onChange={handleInputChange}
                    className={errors.genderCode ? "error" : ""}
                  >
                    <option value="">Select Gender</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                  {errors.genderCode && (
                    <span className="error-message">{errors.genderCode}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="alternateNumber">
                    Alternate Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="alternateNumber"
                    name="alternateNumber"
                    value={formData.alternateNumber}
                    onChange={handleInputChange}
                    className={errors.alternateNumber ? "error" : ""}
                    placeholder="+1-555-0123 (Optional)"
                  />
                  {errors.alternateNumber && (
                    <span className="error-message">
                      {errors.alternateNumber}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="optionalIdentification">Optional ID</label>
                  <input
                    type="text"
                    id="optionalIdentification"
                    name="optionalIdentification"
                    value={formData.optionalIdentification}
                    onChange={handleInputChange}
                    className={errors.optionalIdentification ? "error" : ""}
                    placeholder="Driver's License, Passport, etc. (Optional)"
                  />
                  {errors.optionalIdentification && (
                    <span className="error-message">
                      {errors.optionalIdentification}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="guardianName">Guardian Name *</label>
                  <input
                    type="text"
                    id="guardianName"
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleInputChange}
                    className={errors.guardianName ? "error" : ""}
                    placeholder="Parent/Guardian Full Name"
                  />
                  {errors.guardianName && (
                    <span className="error-message">{errors.guardianName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="guardianMobileNumber">
                    Guardian Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="guardianMobileNumber"
                    name="guardianMobileNumber"
                    value={formData.guardianMobileNumber}
                    onChange={handleInputChange}
                    className={errors.guardianMobileNumber ? "error" : ""}
                    placeholder="+1-555-0123"
                  />
                  {errors.guardianMobileNumber && (
                    <span className="error-message">
                      {errors.guardianMobileNumber}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address *</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? "error" : ""}
                  placeholder="Enter complete address"
                  rows={3}
                />
                {errors.address && (
                  <span className="error-message">{errors.address}</span>
                )}
              </div>
            </div>

            <div className="photo-section">
              <h3>Student Photo</h3>
              <div className="photo-upload-section">
                {formData.photo ? (
                  <div className="photo-preview">
                    {photoPreview && (
                      <img
                        src={photoPreview}
                        alt="Student photo preview"
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: 8,
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <div className="photo-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={removePhoto}
                      >
                        Remove Photo
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => startCamera()}
                      >
                        Take New Photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`photo-upload-area ${
                      isDragOver ? "drag-over" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="upload-content">
                      <div className="upload-icon">
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M17 8L12 3L7 8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 3V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h4>Upload Student Photo</h4>
                      <p>Drag and drop an image here, or click to select</p>
                      <div className="upload-buttons">
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/*"
                          onChange={handleFileInputChange}
                          style={{ display: "none" }}
                        />
                        <label htmlFor="photo-upload" className="btn-secondary">
                          Choose File
                        </label>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startCamera()}
                        >
                          Take Photo
                        </button>
                      </div>
                      <small className="upload-note">
                        Supported formats: JPG, PNG, GIF. Max size: 5MB
                      </small>
                    </div>
                  </div>
                )}
                {errors.photo && (
                  <span className="error-message">{errors.photo}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Registering Student...
                </>
              ) : (
                "Register Student"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div className="camera-modal-overlay" onClick={stopCamera}>
          <div className="camera-modal" onClick={(e) => e.stopPropagation()}>
            <div className="camera-header">
              <h3>Take Student Photo</h3>
              <button className="close-camera" onClick={stopCamera}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="camera-content">
              <video
                id="camera-video"
                autoPlay
                playsInline
                ref={(video) => {
                  if (video && cameraStream) {
                    video.srcObject = cameraStream;
                  }
                }}
              />
              <div className="camera-controls">
                <button className="btn-secondary" onClick={stopCamera}>
                  Cancel
                </button>
                <button
                  className="btn-secondary"
                  onClick={switchCamera}
                  title={
                    facingMode === "user"
                      ? "Switch to back camera"
                      : "Switch to front camera"
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  >
                    <path
                      d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {facingMode === "user" ? "Back Camera" : "Front Camera"}
                </button>
                <button className="btn-primary" onClick={capturePhoto}>
                  Capture Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentRegistration;
